

/**
 * Service for validating workflow nodes and their configurations
 * Tests inputs/outputs and validates configurations before workflow execution
 */
export const nodeValidationService = {
  /**
   * Validate a single node configuration
   * @param {Object} node - Node object to validate
   * @returns {Object} Validation result with status and errors
   */
  validateNode(node) {
    const errors = [];
    const warnings = [];

    // Basic validation
    if (!node?.id) {
      errors?.push('Node ID is missing');
    }

    if (!node?.type) {
      errors?.push('Node type is missing');
    }

    if (!node?.name || node?.name?.trim() === '') {
      errors?.push('Node name is required');
    }

    // Data validation
    if (node?.data) {
      // Duration validation
      if (node?.data?.duration !== undefined && node?.data?.duration !== null) {
        if (typeof node?.data?.duration !== 'number' || node?.data?.duration < 0) {
          errors?.push('Duration must be a positive number');
        }
      }

      // Workers validation
      if (node?.data?.workers !== undefined && node?.data?.workers !== null) {
        if (typeof node?.data?.workers !== 'number' || node?.data?.workers < 0) {
          errors?.push('Workers must be a positive number');
        }
        if (node?.data?.workers === 0) {
          warnings?.push('No workers assigned to this node');
        }
      }

      // Status validation
      const validStatuses = ['pending', 'in-progress', 'completed', 'blocked'];
      if (node?.data?.status && !validStatuses?.includes(node?.data?.status)) {
        errors?.push(`Invalid status: ${node?.data?.status}`);
      }

      // Priority validation
      const validPriorities = ['low', 'medium', 'high', 'critical'];
      if (node?.data?.priority && !validPriorities?.includes(node?.data?.priority)) {
        errors?.push(`Invalid priority: ${node?.data?.priority}`);
      }

      // Container validation
      if (node?.data?.isContainer && (!node?.data?.children || node?.data?.children?.length === 0)) {
        warnings?.push('Container node has no child nodes');
      }
    } else {
      warnings?.push('Node has no configuration data');
    }

    // Position validation
    if (!node?.position || typeof node?.position?.x !== 'number' || typeof node?.position?.y !== 'number') {
      errors?.push('Invalid node position');
    }

    return {
      valid: errors?.length === 0,
      errors,
      warnings,
      nodeId: node?.id,
      nodeName: node?.name
    };
  },

  /**
   * Validate connections between nodes
   * @param {Array} nodes - Array of all nodes
   * @param {Array} connections - Array of connections
   * @returns {Object} Validation result
   */
  validateConnections(nodes, connections) {
    const errors = [];
    const warnings = [];
    const nodeIds = new Set(nodes?.map(n => n?.id));

    connections?.forEach((conn, index) => {
      // Check if connection has required fields
      if (!conn?.source) {
        errors?.push(`Connection ${index + 1}: Missing source node`);
      }
      if (!conn?.target) {
        errors?.push(`Connection ${index + 1}: Missing target node`);
      }

      // Check if source and target nodes exist
      if (conn?.source && !nodeIds?.has(conn?.source)) {
        errors?.push(`Connection ${index + 1}: Source node '${conn?.source}' does not exist`);
      }
      if (conn?.target && !nodeIds?.has(conn?.target)) {
        errors?.push(`Connection ${index + 1}: Target node '${conn?.target}' does not exist`);
      }

      // Check for self-connections
      if (conn?.source === conn?.target) {
        errors?.push(`Connection ${index + 1}: Node cannot connect to itself`);
      }
    });

    // Check for orphaned nodes (nodes with no connections)
    if (nodes?.length > 1) {
      const connectedNodes = new Set();
      connections?.forEach(conn => {
        connectedNodes?.add(conn?.source);
        connectedNodes?.add(conn?.target);
      });

      nodes?.forEach(node => {
        if (!connectedNodes?.has(node?.id)) {
          warnings?.push(`Node '${node?.name}' (${node?.id}) is not connected to any other nodes`);
        }
      });
    }

    // Check for circular dependencies
    const circularPaths = this.detectCircularDependencies(nodes, connections);
    if (circularPaths?.length > 0) {
      warnings?.push(`Detected ${circularPaths?.length} potential circular dependency path(s)`);
    }

    return {
      valid: errors?.length === 0,
      errors,
      warnings
    };
  },

  /**
   * Detect circular dependencies in workflow
   * @param {Array} nodes - Array of nodes
   * @param {Array} connections - Array of connections
   * @returns {Array} Array of circular paths found
   */
  detectCircularDependencies(nodes, connections) {
    const adjacencyList = {};
    const circularPaths = [];

    // Build adjacency list
    nodes?.forEach(node => {
      adjacencyList[node?.id] = [];
    });

    connections?.forEach(conn => {
      if (adjacencyList?.[conn?.source]) {
        adjacencyList?.[conn?.source]?.push(conn?.target);
      }
    });

    // DFS to detect cycles
    const visited = new Set();
    const recursionStack = new Set();

    const dfs = (nodeId, path = []) => {
      visited?.add(nodeId);
      recursionStack?.add(nodeId);
      path?.push(nodeId);

      const neighbors = adjacencyList?.[nodeId] || [];
      for (const neighbor of neighbors) {
        if (!visited?.has(neighbor)) {
          if (dfs(neighbor, [...path])) {
            return true;
          }
        } else if (recursionStack?.has(neighbor)) {
          // Cycle detected
          circularPaths?.push([...path, neighbor]);
          return true;
        }
      }

      recursionStack?.delete(nodeId);
      return false;
    };

    Object.keys(adjacencyList)?.forEach(nodeId => {
      if (!visited?.has(nodeId)) {
        dfs(nodeId);
      }
    });

    return circularPaths;
  },

  /**
   * Validate entire workflow
   * @param {Array} nodes - Array of nodes
   * @param {Array} connections - Array of connections
   * @returns {Object} Complete validation result
   */
  validateWorkflow(nodes, connections) {
    const nodeValidations = [];
    const allErrors = [];
    const allWarnings = [];

    // Validate each node
    nodes?.forEach(node => {
      const result = this.validateNode(node);
      nodeValidations?.push(result);
      if (result?.errors?.length > 0) {
        allErrors?.push(...result?.errors?.map(err => `${node?.name}: ${err}`));
      }
      if (result?.warnings?.length > 0) {
        allWarnings?.push(...result?.warnings?.map(warn => `${node?.name}: ${warn}`));
      }
    });

    // Validate connections
    const connectionValidation = this.validateConnections(nodes, connections);
    if (connectionValidation?.errors?.length > 0) {
      allErrors?.push(...connectionValidation?.errors);
    }
    if (connectionValidation?.warnings?.length > 0) {
      allWarnings?.push(...connectionValidation?.warnings);
    }

    // Check if workflow is empty
    if (nodes?.length === 0) {
      allWarnings?.push('Workflow is empty');
    }

    return {
      valid: allErrors?.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      nodeValidations,
      connectionValidation,
      summary: {
        totalNodes: nodes?.length,
        totalConnections: connections?.length,
        validNodes: nodeValidations?.filter(v => v?.valid)?.length,
        invalidNodes: nodeValidations?.filter(v => !v?.valid)?.length,
        errorCount: allErrors?.length,
        warningCount: allWarnings?.length
      }
    };
  },

  /**
   * Test node with sample inputs
   * @param {Object} node - Node to test
   * @param {Object} testInputs - Sample inputs for testing
   * @returns {Object} Test result
   */
  testNode(node, testInputs = {}) {
    const validationResult = this.validateNode(node);
    
    if (!validationResult?.valid) {
      return {
        success: false,
        message: 'Node validation failed',
        errors: validationResult?.errors,
        warnings: validationResult?.warnings
      };
    }

    // Simulate node execution
    const testResult = {
      success: true,
      message: 'Node test passed',
      nodeId: node?.id,
      nodeName: node?.name,
      inputs: testInputs,
      outputs: {
        status: node?.data?.status || 'pending',
        duration: node?.data?.duration || 0,
        workers: node?.data?.workers || 0,
        timestamp: new Date()?.toISOString()
      },
      warnings: validationResult?.warnings
    };

    return testResult;
  },

  /**
   * Get validation summary for display
   * @param {Object} validationResult - Result from validateWorkflow
   * @returns {String} Human-readable summary
   */
  getValidationSummary(validationResult) {
    if (!validationResult) return 'No validation data';

    const { summary, errors, warnings } = validationResult;
    
    let summaryText = `Workflow Validation Summary:\n`;
    summaryText += `- Total Nodes: ${summary?.totalNodes}\n`;
    summaryText += `- Valid Nodes: ${summary?.validNodes}\n`;
    summaryText += `- Invalid Nodes: ${summary?.invalidNodes}\n`;
    summaryText += `- Total Connections: ${summary?.totalConnections}\n`;
    summaryText += `- Errors: ${summary?.errorCount}\n`;
    summaryText += `- Warnings: ${summary?.warningCount}\n`;

    if (errors?.length > 0) {
      summaryText += `\nErrors:\n`;
      errors?.forEach((err, i) => {
        summaryText += `  ${i + 1}. ${err}\n`;
      });
    }

    if (warnings?.length > 0) {
      summaryText += `\nWarnings:\n`;
      warnings?.forEach((warn, i) => {
        summaryText += `  ${i + 1}. ${warn}\n`;
      });
    }

    return summaryText;
  }
};

export default nodeValidationService;